import { memo, useEffect, useState, useRef, useCallback } from 'react';
import { noop, isEmpty, isNil, first, last } from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CJReferenceOptionValidationSchema } from 'validators/schemas.validation';
import { yupSchemaValidate } from 'validators/yupUtils';
import { getReferencePropertyErrorMessage, type ReferencePropertyErrorMessage } from 'validators/service.validator';
// Import Constants
import { l, defaultLogicFunction } from 'constants/common';
// Import UI Components
import { Popover, Button, Option, Icon, Tooltip, Empty, Dropdown, ExtendedInput } from '@geneui/components';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Services
import { getCustomerJourneyBlockTranslatableErrorText } from 'services/customerJourney/helpers';
import { scrollRefIntoView } from 'services/Helpers';
// Import Styles
import 'assets/scss/cascadeDropdown.scss';

export interface CJCascadeDropdownProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: { label: string; value: any; children: AnyObject[] }[];
    value?: Nullable<AnyObject>;
    node: AnyObject;
    elements: AnyObject[];
    option?: AnyObject;
    onChange?: (value: AnyObject) => void;
    onValidationChange?: (isValid: boolean) => void;
    isDisabled?: boolean;
    onClear?: () => void;
    isEmptyValueValid?: boolean;
    label?: string;
}

const CJCascadeDropdown: React.FC<CJCascadeDropdownProps> = (props) => {
    const {
        data,
        value,
        onChange = noop,
        option,
        node,
        elements,
        onValidationChange = noop,
        isDisabled,
        isEmptyValueValid = false,
        onClear,
        label,
    } = props;
    const { t } = useTranslation();
    // an optional empty reference is valid from the first render, otherwise the field would flash as invalid
    const [isValid, setIsValid] = useState(isEmptyValueValid && isNil(value?.parentValue) && isNil(value?.value));
    const [errorMessages, setErrorMessages] = useState<ReferencePropertyErrorMessage[]>([]);
    const [selectedLogicFunction, setSelectedLogicFunction] = useState<Nullable<AnyObject>>(null);

    const selectedOptionValidation = (value: Nullable<AnyObject>, selectedLogicFunction?: Nullable<AnyObject>) => {
        let tmpBlockId = null;
        let tmpPropertyName = null;
        let tmpFunctionName = null;
        let tmpBaseTypes = null;
        if (!isNil(value)) {
            if (!isNil(value.parentValue)) {
                tmpBlockId = value.parentValue;
            }
            if (!isNil(value.value)) {
                tmpPropertyName = value.value;
            }
            if (!isNil(value?.logicFunction)) {
                tmpFunctionName = value.logicFunction;

                tmpBaseTypes = isNil(selectedLogicFunction?.returnType)
                    ? option?.BaseTypes
                    : selectedLogicFunction?.returnType?.BaseTypes;
            }
        }

        if (isEmptyValueValid && isNil(tmpBlockId) && isNil(tmpPropertyName)) {
            setErrorMessages([]);
            setIsValid(true);
            return;
        }

        const schema = CJReferenceOptionValidationSchema(
            elements,
            node,
            isNil(tmpBaseTypes) ? '' : last(((first(tmpBaseTypes) ?? []) as string).split('.')),
            option?.ModelRelationScope,
            tmpBlockId === node?.id
                ? {
                      typeName: option?.SelfModelTypeName,
                      properties: option?.SelfModelProperties?.Properties,
                  }
                : {},
        );
        const tmpValidationResult = yupSchemaValidate(
            schema,
            { blockId: tmpBlockId, propertyName: tmpPropertyName, logicFunction: tmpFunctionName },
            (e: AnyObject) => {
                setErrorMessages([getReferencePropertyErrorMessage(e)]);
            },
        );

        setIsValid(() => tmpValidationResult);
    };

    useEffect(() => {
        onValidationChange(isValid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]);

    const cascadeDropdownRef = useRef(null);
    const valueInputRef = useRef<HTMLInputElement>(null);
    const searchHandlerTimeout = useRef<Nullable<NodeJS.Timeout>>(null);

    const [isOpened, setIsOpened] = useState(false);
    const [selectedOptionLabel, setSelectedOptionLabel] = useState('');
    const [selectedOption, setSelectedOption] = useState<Nullable<AnyObject>>(null);
    const [selectedParent, setSelectedParent] = useState<Nullable<AnyObject>>(null);
    const [isVisibleChildrenList, setIsVisibleChildrenList] = useState(false);
    const [childrenList, setChildrenList] = useState<AnyObject[]>([]);
    const [allChildrenList, setAllChildrenList] = useState<AnyObject[]>([]);
    const [logicFunctionsData, setLogicFunctionsData] = useState<AnyObject[]>([]);
    const [isSearchResult, setIsSearchResult] = useState(false);

    const showParents = () => {
        setIsVisibleChildrenList(false);
        setChildrenList([]);
        setSelectedParent(null);
        setIsSearchResult(false);
    };

    const open = () => {
        if (isOpened) return;
        setIsOpened(true);
        if (valueInputRef.current) {
            valueInputRef.current.focus();
            valueInputRef.current.selectionStart = selectedOptionLabel?.length || 0;
        }
    };

    const close = () => {
        setIsOpened(false);
        setIsVisibleChildrenList(false);
        showParents();
    };

    const clearClickHandler = () => {
        setSelectedOption(null);
        setSelectedOptionLabel('');
        setSelectedParent(null);
        setSelectedLogicFunction(null);
        setIsVisibleChildrenList(false);
        selectedOptionValidation(null);
        onClear?.();
    };

    const toggleDropdown = () => {
        if (isOpened) {
            close();
        } else {
            open();
        }
    };

    useOnClickOutside(
        cascadeDropdownRef,
        useCallback(() => {
            if (isOpened) {
                close();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isOpened]),
    );

    const onSelectParent = (label, value, children, parentIndex) => {
        return () => {
            setIsVisibleChildrenList(true);
            setChildrenList(children);
            setSelectedParent({ label, value, children, parentIndex });
        };
    };

    const onSelectOption = (label, value, optionIndex, parentLabel, parentValue, parentIndex, logicFunctions) => {
        return () => {
            // const { label: parentLabel, value: parentValue, parentIndex } = selectedParent;
            setLogicFunctionsData(
                logicFunctions.map((logicFunction) => ({
                    ...logicFunction,
                    label: t(logicFunction.label),
                })),
            );
            const tmpLogicFunction = first(logicFunctions) as Nilable<AnyObject>;
            const optionValue: AnyObject = {
                label,
                value,
                parentLabel,
                parentValue,
                parentIndex,
                optionIndex,
                logicFunction: tmpLogicFunction?.value,
            };
            const tmpSelectedLogicFunction = {
                value: tmpLogicFunction?.value,
                returnType: tmpLogicFunction?.returnType,
            };
            setSelectedLogicFunction(tmpSelectedLogicFunction);
            setSelectedOption(optionValue);
            setSelectedParent({ label, value, children: data[parentIndex]?.children, parentIndex });

            setSelectedOptionLabel(`${selectedParent?.label} / ${t(optionValue?.label)}`);

            selectedOptionValidation(optionValue, tmpSelectedLogicFunction);
            onChange(optionValue);
            close();
        };
    };

    const getIsOptionSelected = (option) => {
        return option?.value === selectedOption?.value && option?.parentValue === selectedOption?.parentValue;
    };

    useEffect(() => {
        if (isNil(value)) {
            selectedOptionValidation(value ?? null);
            return;
        }

        const parentDataIndex = data.findIndex((parent) => parent.value === value?.parentValue);
        let tmpSelectedLogicFunction: Nullable<AnyObject> = null;

        const parentData = data[parentDataIndex];

        const isSelectedOptionExists = parentData?.children.some((option) => option.value === value?.value);

        if (parentDataIndex !== -1) {
            if (isSelectedOptionExists) {
                setSelectedParent({
                    label: parentData.label,
                    value: parentData.value,
                    children: parentData.children,
                    parentIndex: parentDataIndex,
                });

                const optionDataIndex = parentData.children.findIndex((option) => option.value === value?.value);
                const optionData = parentData.children[optionDataIndex];
                const optionValue = {
                    parentLabel: parentData.label,
                    parentValue: parentData.value,
                    parentIndex: parentDataIndex,
                    label: optionData.label,
                    value: optionData.value,
                    optionIndex: optionDataIndex,
                    logicFunction: value?.logicFunction,
                };

                const tmpFunction = optionData.logicFunctions.find((func) => func.value === value?.logicFunction);
                tmpSelectedLogicFunction = { value: tmpFunction?.value, returnType: tmpFunction?.returnType };
                setSelectedLogicFunction(tmpSelectedLogicFunction);
                setSelectedOption(optionValue);

                setSelectedOptionLabel(`${parentData?.label} / ${t(optionData?.label)}`);

                setLogicFunctionsData(
                    optionData.logicFunctions.map((logicFunction) => ({
                        ...logicFunction,
                        label: t(logicFunction.label),
                    })),
                );
            }
        } else {
            const selectedElement = elements.find((el) => el.id === value?.parentValue);
            if (!isNil(selectedElement)) {
                // TODO: delete when block delete must be delete all reference in api model
                const tmpProperties =
                    !isNil(option?.SelfModelTypeName) && node?.id === selectedElement?.id
                        ? option?.SelfModelProperties
                        : selectedElement?.data?.metaData?.Properties;
                if (!isNil(tmpProperties)) {
                    const tmpProperty = tmpProperties.find((prop) => prop.Name === value?.value);
                    if (!isNil(tmpProperty)) {
                        setSelectedOptionLabel(`${selectedElement?.data.name} / ${t(tmpProperty?.Name)}`);
                        if (!isNil(tmpProperty?.Functions)) {
                            const tmpFunction = tmpProperty.Functions.find(
                                (logicFunction) => logicFunction.value === value?.logicFunction,
                            );
                            tmpSelectedLogicFunction = {
                                value: tmpFunction?.value,
                                returnType: tmpFunction.returnType,
                                isDisabled: true,
                            };
                            setSelectedLogicFunction(tmpSelectedLogicFunction);
                            setLogicFunctionsData(
                                tmpProperty.Functions.map((logicFunction) => ({
                                    ...logicFunction,
                                    label: t(logicFunction.label),
                                })),
                            );
                        }
                    }
                }
            }
        }
        selectedOptionValidation(value, tmpSelectedLogicFunction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const searchHandler = (searchString) => {
        setSelectedOptionLabel(searchString);

        const optionLabel: string = last(searchString?.split(' / ')) ?? '';

        if (searchHandlerTimeout.current) {
            clearTimeout(searchHandlerTimeout.current);
        }

        searchHandlerTimeout.current = setTimeout(() => {
            setIsVisibleChildrenList(true);
            setIsSearchResult(true);
            setChildrenList(
                allChildrenList.filter(({ label }) => {
                    return (
                        t(label).toLowerCase().includes(optionLabel.toLowerCase()) ||
                        optionLabel.toLowerCase() === t(label).toLowerCase()
                    );
                }),
            );
        }, 300);
    };

    useEffect(() => {
        if (!isEmpty(data)) {
            setAllChildrenList(
                data.reduce<AnyObject[]>((acc, { children }, index) => {
                    const parentOption = data[index];
                    return acc.concat(
                        children.map((child, childIndex) => {
                            child.optionIndex = childIndex;
                            child.parentIndex = index;
                            child.parentLabel = parentOption.label;
                            child.parentValue = parentOption.value;
                            return child;
                        }),
                    );
                }, []),
            );
        }
    }, [data]);

    const logicFunctionChangeHandler = (event) => {
        const tmpValue = event?.value;
        setSelectedLogicFunction({ value: tmpValue, returnType: event?.returnType });
        setSelectedOption({ ...selectedOption, logicFunction: tmpValue });
        onChange({ ...selectedOption, logicFunction: tmpValue });
    };

    return (
        <>
            <div ref={cascadeDropdownRef} className={classNames('crm-cascade-dropdown', { active: isOpened })}>
                <div className={classNames('crm-cascade-dropdown-input', { invalid: !isValid })}>
                    <Tooltip
                        text={selectedOptionLabel}
                        position="top"
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        transitionDuration={0.001}
                    >
                        <span>
                            <ExtendedInput
                                ref={valueInputRef}
                                className="crm-cascade-dropdown-input--input-container"
                                onFocus={open}
                                onChange={({ currentTarget }) => searchHandler(currentTarget?.value)}
                                value={selectedOptionLabel}
                                placeholder={label ?? t(l.Property)}
                                label={label ?? t(l.Property)}
                                labelAppearance="swap"
                                isValid={isDisabled || isValid}
                                errorText={getCustomerJourneyBlockTranslatableErrorText(t, errorMessages)}
                                disabled={isDisabled}
                            />
                        </span>
                    </Tooltip>
                    {!isNil(onClear) && !isEmpty(selectedOptionLabel) && (
                        <Button
                            className="crm-cascade-dropdown-clear"
                            appearance="minimal"
                            icon="bc-icon-clear-small"
                            onClick={clearClickHandler}
                        />
                    )}
                    <Button appearance="minimal" icon="bc-icon-arrow-down" onClick={toggleDropdown} />
                </div>
                {isOpened && (
                    <Popover
                        isOpen={isOpened}
                        extendTargetWidth={false}
                        maxHeight={206}
                        height={206}
                        scrollbarNeeded={true}
                    >
                        <div className="crm-cascade-dropdown-content">
                            {isEmpty(data) ? (
                                <Empty title={t(l.NoDataToDisplay)} />
                            ) : (
                                <>
                                    {!isVisibleChildrenList ? (
                                        data.map(({ label, value, children }, parentIndex) => {
                                            const isParentSelected = value === selectedOption?.parentValue;
                                            return (
                                                <div
                                                    key={parentIndex}
                                                    ref={isParentSelected ? scrollRefIntoView : noop}
                                                >
                                                    <Tooltip
                                                        text={label}
                                                        position="right"
                                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                        // @ts-ignore
                                                        transitionDuration={0.001}
                                                    >
                                                        <Option
                                                            key={parentIndex}
                                                            className={classNames({ active: isParentSelected })}
                                                            content={
                                                                <ul className="option-texts">
                                                                    <li>
                                                                        <div className="option-title ellipsis-text">
                                                                            {label}
                                                                        </div>
                                                                    </li>
                                                                </ul>
                                                            }
                                                            rightCustomElement={<Icon type="bc-icon-arrow-right" />}
                                                            onClick={onSelectParent(
                                                                label,
                                                                value,
                                                                children,
                                                                parentIndex,
                                                            )}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div>
                                            {!isSearchResult && (
                                                <Tooltip
                                                    text={selectedParent?.label}
                                                    position="right"
                                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                    // @ts-ignore
                                                    transitionDuration={0.001}
                                                >
                                                    <Option
                                                        border="bottom"
                                                        content={
                                                            <>
                                                                <div className="option-left-elements">
                                                                    <Icon type="bc-icon-arrow-left" />
                                                                </div>
                                                                <ul className="option-texts">
                                                                    <li>
                                                                        <div className="option-title ellipsis-text">
                                                                            {selectedParent?.label}
                                                                        </div>
                                                                    </li>
                                                                </ul>
                                                            </>
                                                        }
                                                        onClick={showParents}
                                                    />
                                                </Tooltip>
                                            )}

                                            {childrenList.map((option) => {
                                                const {
                                                    label,
                                                    value,
                                                    optionIndex,
                                                    parentLabel,
                                                    parentValue,
                                                    parentIndex,
                                                    logicFunctions,
                                                } = option;

                                                const title = isSearchResult
                                                    ? `${parentLabel} / ${t(label)}`
                                                    : t(label);

                                                const isOptionSelected = getIsOptionSelected(option);

                                                return (
                                                    <div
                                                        key={`${optionIndex}_${parentLabel}_${label}`}
                                                        ref={isOptionSelected ? scrollRefIntoView : noop}
                                                    >
                                                        <Tooltip
                                                            text={title}
                                                            position="right"
                                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                            // @ts-ignore
                                                            transitionDuration={0.001}
                                                        >
                                                            <Option
                                                                key={`${optionIndex}_${parentLabel}_${label}`}
                                                                className={classNames({ active: isOptionSelected })}
                                                                content={
                                                                    <ul className="option-texts">
                                                                        <li>
                                                                            <div className="option-title ellipsis-text">
                                                                                {title}
                                                                            </div>
                                                                        </li>
                                                                    </ul>
                                                                }
                                                                onClick={onSelectOption(
                                                                    label,
                                                                    value,
                                                                    optionIndex,
                                                                    parentLabel,
                                                                    parentValue,
                                                                    parentIndex,
                                                                    logicFunctions,
                                                                )}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Popover>
                )}
            </div>

            <div className="crm-cascade-dropdown-function">
                {!isEmpty(logicFunctionsData) &&
                    (logicFunctionsData?.length > 1 || first(logicFunctionsData)?.value !== defaultLogicFunction) && (
                        <Dropdown
                            isMultiSelect={false}
                            disabled={selectedLogicFunction?.isDisabled === true || isDisabled}
                            hasSearch={true}
                            inputSize="default"
                            placeholder={t(l.Function)}
                            label={t(l.Function)}
                            labelAppearance="swap"
                            appearance="outline"
                            data={logicFunctionsData}
                            onChange={logicFunctionChangeHandler}
                            value={selectedLogicFunction?.value}
                            searchPlaceholderText={t(l.Search)}
                            noDataText={t(l.NoDataFound)}
                        />
                    )}
            </div>
        </>
    );
};

export default memo(CJCascadeDropdown);
